import {
  Accordion,
  Checkbox,
  cn,
  Field,
  FieldError,
  FieldLabel,
  Input,
  RadioGroup,
  RadioItem,
  Select,
  Text,
  Textarea as AtomicTextarea,
} from '@risksmart-app/atomic-ui';
import {
  type ForwardedRef,
  forwardRef,
  useCallback,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import type { FieldEditorValues, SectionEditorValues } from '../config';
import type { FieldSummary } from '../constants';
import {
  collectValidationErrors,
  type PreviewValidationContext,
} from '../formPreviewUtils';
import type { FormPreviewHandle } from '../types';

export type { FormPreviewHandle };

const NO_IDS: ReadonlySet<string> = new Set();

/** Both spellings reach this block; hosts have used either. */
const SELECT_FIELD_TYPES: ReadonlySet<string> = new Set(['select', 'dropdown']);

const isSelectFieldType = (type: string): boolean =>
  SELECT_FIELD_TYPES.has(type);

const PreviewField = ({
  field,
  fieldConfig,
  value,
  onChange,
  error,
}: {
  field: FieldSummary;
  fieldConfig?: FieldEditorValues;
  value: unknown;
  onChange: (value: unknown) => void;
  error?: string;
}) => {
  const options = fieldConfig?.options ?? [];
  const inputId = useId();
  const isInputType =
    field.type === 'text' ||
    field.type === 'url' ||
    field.type === 'number' ||
    field.type === 'textArea';

  const invalid = !!error;

  const readOnlyDisplayValue = (() => {
    if (isSelectFieldType(field.type)) {
      const opt = options.find((o) => o.id === String(value ?? ''));

      return opt?.label || String(value ?? '—');
    }
    if (field.type === 'radio') {
      const opt = options.find((o) => o.id === String(value ?? ''));

      return opt?.label || String(value ?? '—');
    }
    if (field.type === 'multiselect') {
      const selected = Array.isArray(value) ? value : [];
      const labels = selected
        .map((v) => options.find((o) => o.id === v)?.label ?? v)
        .filter(Boolean);

      return labels.length > 0 ? labels.join(', ') : '—';
    }

    return String(value ?? '—');
  })();

  if (field.readOnly) {
    return (
      <Field>
        <FieldLabel>{field.name}</FieldLabel>
        <Text preset={'body-md'} className={'text-lg text-primary'}>
          {readOnlyDisplayValue}
        </Text>
      </Field>
    );
  }

  return (
    <Field data-invalid={error ? true : undefined}>
      <FieldLabel htmlFor={isInputType ? inputId : undefined}>
        {field.name}
        {field.required ? (
          <span className={'text-destructive ml-0.5'}>{'*'}</span>
        ) : null}
      </FieldLabel>
      {field.type === 'text' ||
      field.type === 'link' ||
      field.type === 'number' ||
      field.type === 'date' ? (
        <Input
          id={inputId}
          placeholder={'Placeholder text'}
          value={String(value ?? '')}
          onChange={(e) => onChange(e.target.value)}
          aria-invalid={invalid}
          type={
            field.type === 'date'
              ? 'date'
              : field.type === 'number'
                ? 'number'
                : 'text'
          }
        />
      ) : null}
      {field.type === 'textarea' ? (
        <AtomicTextarea
          id={inputId}
          placeholder={'Placeholder text'}
          rows={3}
          value={String(value ?? '')}
          onChange={(e) => onChange(e.target.value)}
          aria-invalid={invalid}
        />
      ) : null}
      {field.type === 'radio' ? (
        <RadioGroup
          value={String(value ?? '')}
          onValueChange={(v) => onChange(v)}
        >
          {options.map((opt, i) => (
            <label
              key={opt.id}
              className={'flex items-center gap-2 text-sm cursor-pointer'}
            >
              <RadioItem value={opt.id} />
              {opt.label || `Option ${i + 1}`}
            </label>
          ))}
        </RadioGroup>
      ) : null}
      {isSelectFieldType(field.type) ? (
        <Select
          items={[
            { label: 'Select...', value: null },
            ...options.map((opt) => ({
              label: opt.label || `Option`,
              value: opt.id,
            })),
          ]}
          value={String(value ?? '')}
          onValueChange={(v) => onChange(v)}
          aria-invalid={invalid}
        />
      ) : null}
      {field.type === 'multiselect' ? (
        <div
          className={cn(
            'flex flex-col gap-1.5 rounded-lg border p-3',
            invalid ? 'border-destructive' : 'border-neutral-border'
          )}
        >
          {options.map((opt, i) => {
            const checked = Array.isArray(value)
              ? value.includes(opt.id)
              : false;

            return (
              <label
                key={opt.id}
                className={'flex items-center gap-2 text-sm cursor-pointer'}
              >
                <Checkbox
                  size={'md'}
                  checked={checked}
                  onCheckedChange={(isChecked) => {
                    const current = Array.isArray(value) ? value : [];
                    onChange(
                      isChecked
                        ? [...current, opt.id]
                        : current.filter((v: string) => v !== opt.id)
                    );
                  }}
                />
                {opt.label || `Option ${i + 1}`}
              </label>
            );
          })}
        </div>
      ) : null}
      {error ? <FieldError>{error}</FieldError> : null}
    </Field>
  );
};

interface FormPreviewProps {
  containerOrder: string[];
  containers: Record<string, string[]>;
  fields: Record<string, FieldSummary>;
  sectionNames: Record<string, string>;
  fieldConfigs: Record<string, FieldEditorValues>;
  sectionConfigs: Record<string, SectionEditorValues>;
  hiddenFieldIds?: ReadonlySet<string>;
  hiddenSectionIds?: ReadonlySet<string>;
  onDataChange?: () => void;
}

const FormPreviewInner = (
  props: FormPreviewProps,
  ref: ForwardedRef<FormPreviewHandle>
) => {
  const {
    containerOrder,
    containers,
    fields,
    sectionNames,
    fieldConfigs,
    hiddenFieldIds = NO_IDS,
    hiddenSectionIds = NO_IDS,
    onDataChange,
  } = props;
  const [openSections, setOpenSections] = useState<string[]>(
    containerOrder.length > 0 ? [containerOrder[0]] : []
  );
  const [previewData, setPreviewData] = useState<Record<string, unknown>>(
    () => {
      const defaults: Record<string, unknown> = {};
      for (const [id, config] of Object.entries(fieldConfigs)) {
        if (config?.defaultValue) {
          defaults[id] = config.defaultValue;
        }
      }

      return defaults;
    }
  );
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});
  const [hasValidated, setHasValidated] = useState(false);
  const handleChange = useCallback(
    (fieldId: string, value: unknown) => {
      setPreviewData((prev) => ({ ...prev, [fieldId]: value }));
      setHasValidated(false);
      onDataChange?.();
      // Clear error for this field when user changes it
      setValidationErrors((prev) => {
        if (prev[fieldId]) {
          const next = { ...prev };
          delete next[fieldId];

          return next;
        }

        return prev;
      });
    },
    [onDataChange]
  );

  const validationCtx = useMemo<PreviewValidationContext>(
    () => ({
      fieldConfigs,
      previewData,
      fields,
      containers,
      hiddenFieldIds,
      hiddenSectionIds,
    }),
    [
      fieldConfigs,
      previewData,
      fields,
      containers,
      hiddenFieldIds,
      hiddenSectionIds,
    ]
  );
  const validationCtxRef = useRef(validationCtx);
  validationCtxRef.current = validationCtx;

  const validate = useCallback(() => {
    const errors = collectValidationErrors(
      containerOrder,
      validationCtxRef.current
    );

    setValidationErrors(errors);
    setHasValidated(true);

    return Object.keys(errors).length === 0;
  }, [containerOrder]);

  useImperativeHandle(ref, () => ({ validate }), [validate]);

  const getSectionValidationStatus = (
    sectionId: string
  ): 'valid' | 'invalid' | null => {
    if (!hasValidated) {
      return null;
    }
    const fieldIds = containers[sectionId] ?? [];
    const hasError = fieldIds.some((id) => validationErrors[id]);

    return hasError ? 'invalid' : 'valid';
  };

  return (
    <div className={cn('flex flex-col gap-4')}>
      <Accordion multiple value={openSections} onValueChange={setOpenSections}>
        {containerOrder.map((sectionId) => {
          if (hiddenSectionIds.has(sectionId)) {
            return null;
          }

          const fieldIds = containers[sectionId] ?? [];

          const sectionStatus = getSectionValidationStatus(sectionId);

          return (
            <Accordion.Item
              key={sectionId}
              value={sectionId}
              className={cn(
                'contain-paint transition-colors duration-300',
                sectionStatus === 'valid' && 'border-secondary',
                sectionStatus === 'invalid' && 'border-destructive'
              )}
            >
              <Accordion.Header>
                <Accordion.Trigger>
                  {sectionNames[sectionId] ?? sectionId}
                </Accordion.Trigger>
              </Accordion.Header>
              <Accordion.Content>
                <div className={cn('flex flex-col gap-4')}>
                  {fieldIds.map((fieldId) => {
                    const field = fields[fieldId];
                    if (!field) {
                      return null;
                    }

                    // Hidden fields are completely invisible in preview
                    if (fieldConfigs[fieldId]?.hidden) {
                      return null;
                    }

                    if (hiddenFieldIds.has(fieldId)) {
                      return null;
                    }

                    return (
                      <PreviewField
                        key={fieldId}
                        field={field}
                        fieldConfig={fieldConfigs[fieldId]}
                        value={previewData[fieldId]}
                        onChange={(v) => handleChange(fieldId, v)}
                        error={validationErrors[fieldId]}
                      />
                    );
                  })}
                </div>
              </Accordion.Content>
            </Accordion.Item>
          );
        })}
      </Accordion>
    </div>
  );
};

export const FormPreview = forwardRef(FormPreviewInner);
